/** packages */
import uniq from 'lodash/uniq'
import cloneDeep from 'lodash/cloneDeep'
import { format, parseISO } from 'date-fns'

/** constants */
import { MARKET_TYPE } from '~/src/constants/markets'
import {
  REPORT_ASSESSMENT_DATE,
  REPORT_FILE_EXTENSION,
  REPORT_TAGS,
  REPORT_TAGS_VIDEO_INTRO,
} from '~/src/constants/reports'

/** types */
import type { TCommon } from '~/src/types/common'
import type {
  TReport,
  TReportAssessmentDate,
  TReportCard,
  TReportFileExtension,
  TReportTag,
  TReportTaxonomy,
} from '~/src/types/reports'

export const getReportCard = (
  report: TReport,
  denyValidation = false,
): TReportCard => {
  const overlayMarketTypes: string[] = [
    MARKET_TYPE.PRICES_ASSESSMENTS,
    MARKET_TYPE.MARKET_ASSESSMENTS,
  ]
  return {
    ...report,
    show_overlay: overlayMarketTypes.includes(report.market_type),
    hide_label:
      report.tags?.length &&
      isReportValidByTags(report, REPORT_TAGS, denyValidation),
    hide_border: false,
  }
}

export const getReportCards = (
  reports: TReport[],
  pageLimit: number = 10,
): TReportCard[] => {
  return reports
    .filter(
      (report) =>
        !report.tags?.length ||
        isReportValidByTags(report, REPORT_TAGS_VIDEO_INTRO, true),
    )
    .map((_report) => getReportCard(_report, true))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, pageLimit)
}

/**
 * Get reports for the latest reports section
 *
 * @returns {Object} - Object containing all PDF reports, latest reports and rest of reports
 */
export const getReportsForLatestReportsSection = (
  reports: TReport[],
): {
  allPdfReports: TReportCard[]
  latestReports: TReportCard[]
  restOfReports: TReportCard[]
} => {
  const allPdfReports = reports
    .filter((_report) =>
      isReportValidByFileExtension(_report, [REPORT_FILE_EXTENSION.PDF]),
    )
    .map((_report) => getReportCard(_report))
  const allExcelReports = reports
    .filter((_report) =>
      isReportValidByFileExtension(_report, [
        REPORT_FILE_EXTENSION.XLSX,
        REPORT_FILE_EXTENSION.XLS,
      ]),
    )
    .map((_report) => getReportCard(_report))

  const reportsPdf = cloneDeep(allPdfReports)
  const firstReportPdf = reportsPdf.shift()
  const firstReportExcel = allExcelReports.shift()
  const latestReports = [firstReportPdf, firstReportExcel]
    .filter(Boolean)
    .map((_report) => getReportCard(_report))
  const restOfReports = allPdfReports
    .slice(1)
    .map((_report) => getReportCard(_report))

  return { allPdfReports, latestReports, restOfReports }
}

const isReportValidByFileExtension = (
  report: TReport,
  types: TReportFileExtension[],
) => types.includes(report.file_extension)

export const isReportValidByTags = (
  report: TReport,
  tags: string[],
  denyValidation = false,
) => {
  if (!report.tags?.length) return false
  const fnTags = denyValidation
    ? (_tag: TReportTaxonomy) => !tags.includes(_tag.name)
    : (_tag: TReportTaxonomy) => tags.some((_tagName) => _tag.name === _tagName)
  return report.tags.some(fnTags)
}

export const isReportValidByTaxonomy = (report: TReportCard, tag: TReportTag) =>
  report.tags?.length && report.tags[0].name === tag

type TFilteredReportsGroups = { match: TCommon[]; noMatch: TCommon[] }

export function filterReports(
  reports: TCommon[],
  search: string,
): TFilteredReportsGroups {
  return reports.reduce(
    (result, item) => {
      const hasMatchSearch = item.tags.some(
        (tag: { name: string }) => tag.name === search,
      )
      if (hasMatchSearch) {
        result.match.push(item)
      } else {
        result.noMatch.push(item)
      }
      return result
    },
    { match: [], noMatch: [] },
  )
}

const parseReports = (reports) =>
  reports.map((report) => ({
    show_overlay: true,
    ...report,
    year: dateToYear(report.date),
  }))

const dateToYear = (date) => {
  return Number(format(parseISO(date), 'yyyy'))
}

export const groupReportsByYear = (reports: TReport[]) => {
  const groups: Record<string, TReport[]> = {}
  parseReports(reports).forEach((report) => {
    groups[report.year] = groups[report.year] || []
    groups[report.year].push(report)
  })
  return groups
}

export const getYears = (reports) => {
  const dates: number[] = reports.map((report) => dateToYear(report.date))
  return [...new Set(dates)].sort((a, b) => b - a)
}

export const getFrequencies = (reports): string[] => {
  const REPORT_ASSESSMENT_DATES: TReportAssessmentDate[] = [
    REPORT_ASSESSMENT_DATE.WEEKLY,
    REPORT_ASSESSMENT_DATE.FULL,
  ]
  const assessmentTypes: TReportAssessmentDate[] = uniq(
    reports.map((report) => report.assessment_type),
  )
  const frequencies: string[] = assessmentTypes.sort(
    (a, b) =>
      REPORT_ASSESSMENT_DATES.indexOf(a) - REPORT_ASSESSMENT_DATES.indexOf(b),
  )

  if (frequencies.length <= 1) return []

  return [REPORT_ASSESSMENT_DATE.ALL, ...frequencies]
}

type TGetReportGroups = {
  currentReports: TReport[]
  currentPage: number
  pageSize: number
  showLatestReportSection?: boolean
}

export const getReportGroups = ({
  currentReports,
  currentPage,
  pageSize,
  showLatestReportSection,
}: TGetReportGroups) => {
  const start = (currentPage - 1) * pageSize
  const end = start + pageSize

  const { allPdfReports, restOfReports, latestReports } =
    getReportsForLatestReportsSection(currentReports)

  const reportsToShow = showLatestReportSection ? restOfReports : allPdfReports
  const reports = reportsToShow.slice(start, end)

  return {
    years: getYears(reports),
    groups: groupReportsByYear(reports),
    latestReports: latestReports,
    reports: reportsToShow,
  }
}

export const getCriticalInsightsGroups = ({
  currentReports,
  currentPage,
  pageSize,
}: TGetReportGroups) => {
  const start = (currentPage - 1) * pageSize
  const end = start + pageSize

  const reports = currentReports.slice(start, end)

  return {
    years: getYears(reports),
    groups: groupReportsByYear(reports),
    reports,
  }
}
